import { useMemo } from 'react';

import { mediasCarouselTracker as tracker } from '@leroy-merlin-br/backpack-analytics';

import { algolia } from '@/infra/tracking/algolia';
import { YoutubePlayer } from '@/presenters/components/catalog';
import { Image } from '@/presenters/components/common/Image';
import { useProductStore } from '@/presenters/store/catalog/product';

import { type Media, Sketch3D, Video } from '../utils/build-visual-medias';

import { SketchFabPlayer } from './components';
import { styles } from './MainItem.styles';

interface MainItemProps {
  media: Media;
  isInView: boolean;
  isActive: boolean;
  onClick: () => void;
  index: number;
}

function MainItem(props: Readonly<MainItemProps>) {
  const { isInView, media, isActive, onClick, index, ...rest } = props;

  const classNames = styles();

  const { _id: productId } = useProductStore((state) => state.product);

  const content = useMemo(() => {
    const isFirstSlide = index === 0;

    if (!isInView && !isFirstSlide) {
      return null;
    }

    if (media instanceof Sketch3D) {
      return (
        <SketchFabPlayer
          modelId={media.modelId}
          className={classNames.sketchFab()}
          onStart={() => {
            tracker.pushStart3d({ productId: productId });
          }}
        />
      );
    }

    if (media instanceof Video) {
      const { alt, youtubeId } = media;

      return (
        <div data-testid="youtube-player">
          <YoutubePlayer
            className={classNames.youtubePlayer()}
            title={alt}
            videoId={youtubeId}
            isActive={isActive}
            onPlay={() => {
              algolia.sendPdpClickEvent();
              tracker.pushStartVideo({ videoId: youtubeId });
            }}
          />
        </div>
      );
    }

    const { alt, src, height, width } = media;

    return (
      <Image
        src={src}
        alt={alt}
        width={width}
        height={height}
        priority={isFirstSlide}
        onClick={() => {
          onClick();
          algolia.sendPdpClickEvent();
          tracker.pushClickSuperZoom({ productId: productId });
        }}
      />
    );
  }, [index, isInView, media, isActive, classNames, productId, onClick]);

  return (
    <div className={classNames.base()} data-is-active={isActive} {...rest}>
      {content}
    </div>
  );
}

export default MainItem;
